// Govern360 Admin - top-level component with staff gating.
//
// Access rules:
//   1. Must be signed in (Google/Azure/email - same auth as console).
//   2. Email must end in @govern360.ai (enforced at login + here).
//   3. Must have a row in staff_roles. Non-staff are redirected to the console.

import { useEffect, useState } from 'react';
import {
  AuthProvider, useAuth, LoadingScreen, LoginPage, AuthCallback, supabase,
} from '@govern360/shared';
import AdminApp from './AdminApp.jsx';

const CONSOLE_URL = 'https://console.govern360.ai';

const LOGIN_CONFIG = {
  variant: 'admin',
  title: 'Internal staff sign-in',
  subtitle: 'This console is for Govern360 employees only. All actions are logged.',
  ssoButtons: ['google', 'azure'],
  emailHint: 'you@govern360.ai',
validateEmail: (email) => {
    const e = email.toLowerCase();
    const allowed = e.endsWith('@govern360.ai') || (import.meta.env.DEV && e === 'rchris72@gmail.com');
    return allowed ? null : 'Internal staff only - a @govern360.ai email is required.';
  },
  warningNode: (
    <div className="admin-warn">
      <i className="ti ti-shield-lock" />
      <span>All access to customer data is recorded with reason, scope, and duration. Continued use is consent to this monitoring.</span>
    </div>
  ),
  footerNode: (
    <>Customer? <a href={CONSOLE_URL}>Sign in to your tenant console &rarr;</a></>
  ),
};

export default function App() {
  return (
    <AuthProvider>
      <AdminContent />
    </AuthProvider>
  );
}

function AdminContent() {
  const { isAuthenticated, user, loading } = useAuth();
  const [path, setPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [staffChecked, setStaffChecked] = useState(false);
  const [isStaff, setIsStaff] = useState(false);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // After auth, verify staff membership.
  useEffect(() => {
    let active = true;
    if (!isAuthenticated || !user?.id) { setStaffChecked(false); setIsStaff(false); return; }
    (async () => {
      // If a super-admin invited this email, promote on first sign-in (no link needed).
      try { await supabase.rpc('claim_staff_invitation'); } catch (e) { /* non-fatal */ }
      const { data } = await supabase.from('staff_roles').select('role').eq('user_id', user.id).maybeSingle();
      if (!active) return;
      setIsStaff(!!data);
      setStaffChecked(true);
    })();
    return () => { active = false; };
  }, [isAuthenticated, user?.id]);

  if (path === '/auth/callback') return <AuthCallback />;
  if (loading) return <LoadingScreen label="Checking your session..." />;
  if (!isAuthenticated) return <LoginPage config={LOGIN_CONFIG} />;
  if (!staffChecked) return <LoadingScreen label="Verifying staff access..." />;

  if (!isStaff) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ borderTop: '4px solid var(--g-admin)', textAlign: 'center' }}>
          <h1 className="login-h1">Not a staff account</h1>
          <p className="login-sub">
            {user?.email} isn&apos;t registered as Govern360 staff. If you&apos;re a customer,
            use the tenant console.
          </p>
          <a className="btn admin-primary block" href={CONSOLE_URL} style={{ marginTop: 8 }}>
            Go to tenant console
          </a>
        </div>
      </div>
    );
  }

  return <AdminApp />;
}
